import React, { useEffect, useState } from "react";
import {
  Box, Typography, Card, CardContent, Grid,
} from "@mui/material";
import ReusableImageComponents from "../../components/reusableComponents/ResuableImageComponents";
import ReusableDateInput from "../../components/reusableComponents/ReusableDateInput";
import RichTextEditor from "../../components/reusableComponents/ResuableRichTextEditor";
import ReusableComponent from "../../components/reusableComponents/reusableInputComponents";
import ApiService from "../../_services/ApiService";
import { validateImageFile } from "../../_helpers/reusablefunctions/validateImageFile";
import { validateSquareImageFile } from "../../_helpers/reusablefunctions/validateImageFile";
import { isValidHttpUrl } from "../../_helpers/validationPage";

const OfferOverview = ({ formData, setFormData, selectedProductFiles,
  setSelectedProductFiles, setSelectedBannerFile, isDataFetched, isEditMode, formErrors, onDelete, setFormErrors, onEdit }) => {
  const [errors, setErrors] = useState({});
  const [offerCategories, setOfferCategories] = useState([]);

  const deliveryOptions = [
    { value: '1', label: '2-5 Days' },
    { value: '2', label: '6-7 Days' },
    { value: '3', label: '8-15 Days' },
    // { value: '15', label: '15 Days' },
  ];

  const SectionTitle = ({ children }) => (
    <Typography sx={{ fontWeight: 500, fontSize: 18, color: '#626478', marginBottom: '10px' }}>
      {children}
    </Typography>
  );

  const handleImageChange = (file, key, setFileCallback, errorKey) => {
    validateImageFile(file, 382, 185, (errorMsg) => {
      if (errorMsg) {
        setFormErrors(prev => ({ ...prev, [errorKey]: errorMsg }));
      } else {
        setFormErrors(prev => ({ ...prev, [errorKey]: null }));
        setFormData(prev => ({ ...prev, [key]: file }));
        setFileCallback(key === 'product_image' ? [file] : file);
      }
    });
  };

  const handleBannerImageChange = (e) => {
    const file = e.target.files[0];
    if (file) handleImageChange(file, 'banner', setSelectedBannerFile, 'banner');
  };


  const handleProductImageChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const totalFiles = selectedProductFiles.length + newFiles.length;

    if (totalFiles > 3) {
      setFormErrors(prev => ({
        ...prev,
        product_images: "You can only upload up to 3 product images."
      }));
      return;
    }
    Promise.all(newFiles.map(file =>
      new Promise((resolve) => {
        validateImageFile(file, 150, 150, (errorMsg) => {
          resolve({ file, errorMsg });
        });
      })
    )).then(results => {
      const errors = results.filter(r => r.errorMsg).map(r => r.errorMsg);
      if (errors.length) {
        setFormErrors(prev => ({ ...prev, product_images: errors.join(', ') }));
      } else {
        setFormErrors(prev => ({ ...prev, product_images: null }));
        setSelectedProductFiles(prev => [...prev, ...newFiles]);
        setFormData(prev => ({
          ...prev,
          product_images: [...(prev.product_images || []), ...newFiles]
        }));
      }
    });
  };


  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (field === "url") {
      let errorMsg = "";
      if (!value || value.trim() === "") {
        errorMsg = "Offer URL is required.";
      } else if (!isValidHttpUrl(value.trim())) {
        errorMsg = "Please enter a valid URL";
      }
      setFormErrors(prev => ({ ...prev, url: errorMsg }));
    } else if (formErrors?.[field]) {
      setFormErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await ApiService.get('offer_category');
        if (res?.data?.results) {
          const formatted = res.data.results.map(({ id, name }) => ({
            value: id,
            label: name,
          }));
          setOfferCategories(formatted);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, []);

  const allImages = [
    ...(selectedProductFiles.length > 0
      ? selectedProductFiles.map(file =>
        typeof file === "string" ? file : URL.createObjectURL(file)
      )
      : (formData.product_images || []))
  ];
  useEffect(() => {
    if (
      isEditMode &&
      formData.offer_category &&
      typeof formData.offer_category === "object" &&
      !formData.offer_category.value
    ) {
      setFormData(prev => ({
        ...prev,
        offer_category: {
          value: formData.offer_category.id,
          label: formData.offer_category.name,
        },
      }));
    }
  }, [isDataFetched, formData.offer_category]);



  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Card sx={{ borderRadius: '10px' }}>
            <CardContent>
              <SectionTitle>Upload Offer Banner *</SectionTitle>
              <Box>
                <ReusableImageComponents
                  selectedFile={formData.banner}
                  handleFileChange={handleBannerImageChange}
                  tooltipTitle="Change Banner Image"
                  error={!!formErrors.banner}
                  helperText={formErrors.banner}
                  caption="JPG,PNG,JPEG | 382 X 185 | Max. 10MB"
                  labelText="Image"
                  errorMsg={"No brands logo ,artist name , social handles"}
                  // hideDeleteIcon={true}
                  hideEditIcon={true}
                  onDelete={() => {
                    setSelectedBannerFile(null);
                    setFormData(prev => ({ ...prev, banner: null }));
                  }}
                />
              </Box>
            </CardContent>

            <CardContent>
              <SectionTitle>Creative Assets</SectionTitle>
              <Typography sx={{ fontWeight: 500, fontSize: 16, color: '#626478', marginBottom: '10px' }}>
                Product Image *
              </Typography>
              <Box>
                <ReusableImageComponents
                  selectedFiles={selectedProductFiles}
                  setSelectedFiles={setSelectedProductFiles}
                  multiple={true}
                  handleFileChange={handleProductImageChange}
                  imageSrcs={allImages}
                  tooltipTitle="Change Product Image"
                  caption={"JPG,PNG | 150 X 150 | Square | Max 10 MB"}
                  error={!!formErrors.product_images}
                  helperText={formErrors.product_images}
                  labelText="Product Image"
                  onDelete={onDelete}
                  onEdit={onEdit}
                  uploadDisabled={selectedProductFiles.length >= 3}
                  editDisabled={false}
                  errorMsg={"Upload only 3 Product Images"}
                />
              </Box>
              <Box sx={{ paddingTop: '20px' }}>
                <RichTextEditor
                  value={formData.product_description}
                  onChange={(val) => handleInputChange("product_description", val)}
                  maxLength={500}
                  error={!!formErrors.product_description}
                  helperText={formErrors.product_description}
                  minHeight={150}
                  title="Product Description"
                  placeholder="Describe your product in detail(max 800 characters)"
                  required={true}
                  width={{ xs: '100%', md: "450px" }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{ borderRadius: '10px' }}>
            <CardContent>
              <SectionTitle>Offer Identity</SectionTitle>
              <Grid container spacing={2}>
                {[{
                  type: "select", label: "Offer Type", value: formData.offer_category,
                  onChange: (v) => handleInputChange("offer_category", v), options: offerCategories,
                  error: !!formErrors.offer_category,
                  helperText: formErrors.offer_category,
                  requireValue: true
                }, {
                  type: "text", label: "Offer Title", placeholder: "e.g., 'Get 50% Off Spotify Premium for Students'",
                  value: formData.title, onChange: (v) => handleInputChange("title", v), requireValue: true,
                  error: !!formErrors.title,
                  helperText: formErrors.title
                }, {
                  type: "text", label: "Meta Description", placeholder: "Brief tagline for campaign (50-100 characters)",
                  value: formData.meta_description, onChange: (v) => handleInputChange("meta_description", v), requireValue: true,
                  error: !!formErrors.meta_description,
                  helperText: formErrors.meta_description
                }, {
                  custom: (
                    <RichTextEditor
                      title="Offer Description"
                      value={formData.offer_description}
                      onChange={(val) => handleInputChange("offer_description", val)}
                      maxLength={500}
                      placeholder={"Describe your offer in detail (max 800 characters)"}
                      minHeight={150}
                      error={!!formErrors.offer_description}
                      helperText={formErrors.offer_description}
                      required={true}
                      width={{ xs: '100%', md: "630px" }}
                    />)
                }, {
                  type: "text", label: "Offer URL", placeholder: "Enter offer URL",
                  value: formData.url, onChange: (v) => handleInputChange("url", v), requireValue: true, error: !!formErrors.url,
                  helperText: formErrors.url
                }].map((field, i) => (
                  <Grid item xs={12} key={i}>
                    {field.custom || (
                      <ReusableComponent {...field} />
                    )}
                  </Grid>
                ))}

                <Grid item xs={12} md={6}>
                  <ReusableDateInput
                    DeadLineDate={formData.start_date}
                    setDeadLineDate={(d) => handleInputChange("start_date", d)}
                    fullWidth
                    label="Campaign Start Date"
                    error={!!formErrors.start_date}
                    helperText={formErrors.start_date}
                    required={true}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ReusableDateInput
                    DeadLineDate={formData.end_date}
                    setDeadLineDate={(d) => handleInputChange("end_date", d)}
                    fullWidth
                    label="Campaign End Date"
                    minDate={formData.start_date || null}
                    error={!!formErrors.end_date}
                    helperText={formErrors.end_date}
                    required={true}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ReusableComponent
                    type="numeric"
                    label="Available Units"
                    placeholder="e.g , 100"
                    value={formData.available_units}
                    onChange={(v) => handleInputChange("available_units", v)}
                    requireValue
                    error={!!formErrors.available_units}
                    helperText={formErrors.available_units}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ReusableComponent
                    type="select"
                    label="Product Delivery Time"
                    placeholder="Select from dropdown"
                    value={formData.delivery_time}
                    onChange={(v) => handleInputChange("delivery_time", v)}
                    options={deliveryOptions}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OfferOverview;
